import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Power, ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { useCapabilitiesStore } from '../stores/capabilitiesStore';
import { Permission } from '../auth/permissions';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Select } from '../components/ui/Select';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { TempPasswordPanel } from './TempPasswordPanel';

type B2bRole = 'B2B_OWNER' | 'B2B_BUYER' | 'B2B_VIEWER';

interface B2bUser {
  id: string;
  email: string;
  role: string;
  full_name: string | null;
  active: boolean;
  created_at: string;
  /** What this account has done — {} for one that has never been used. */
  history?: Record<string, number>;
  /** Server's verdict: an account with history is deactivated, never deleted. */
  deletable?: boolean;
  /** The company's only active owner — deactivating or demoting it is refused. */
  lastActiveOwner?: boolean;
}

/** label/blurb are i18n keys (adminScreens.customerB2bUsers.*), resolved with t() at render. */
const ROLE_OPTIONS: { value: B2bRole; label: string; blurb: string }[] = [
  {
    value: 'B2B_OWNER',
    label: 'adminScreens.customerB2bUsers.roles.B2B_OWNER',
    blurb: 'adminScreens.customerB2bUsers.blurb.B2B_OWNER',
  },
  {
    value: 'B2B_BUYER',
    label: 'adminScreens.customerB2bUsers.roles.B2B_BUYER',
    blurb: 'adminScreens.customerB2bUsers.blurb.B2B_BUYER',
  },
  {
    value: 'B2B_VIEWER',
    label: 'adminScreens.customerB2bUsers.roles.B2B_VIEWER',
    blurb: 'adminScreens.customerB2bUsers.blurb.B2B_VIEWER',
  },
];

/** Server history labels (B2bUserService HISTORY_SOURCES) → i18n plural key stems. */
const HISTORY_KEY: Record<string, string> = {
  order: 'order',
  quote: 'quote',
  return: 'return',
  'discount request': 'discountRequest',
  payment: 'payment',
  'audited action': 'auditedAction',
};
const cid = (): string => crypto.randomUUID();

/**
 * B2B user management for a single customer, embedded on the customer detail page (the "Users"
 * tab). The customerId comes from the page context — no UUID to type. Create is gated on the
 * B2B_USER_CREATE capability (OWNER/ADMIN/MANAGER); B2B roles themselves can never create.
 * A new account is provisioned with a one-time temp password (shown once) and forced to change
 * it at first login.
 */
export default function CustomerB2bUsers({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const perms = useCapabilitiesStore((s) => s.permissionSet);
  const canCreate = perms.has(Permission.B2B_USER_CREATE);

  const [confirm, confirmDialog] = useConfirm();
  const [users, setUsers] = useState<B2bUser[]>([]);
  const [loading, setLoading] = useState(true);
  /** The last temporary password issued here — from a create OR a reset. */
  const [issued, setIssued] = useState<{
    email: string;
    password: string;
    emailSent?: boolean;
    emailError?: string;
  } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<B2bRole>('B2B_BUYER');
  const [busy, setBusy] = useState(false);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<B2bUser[]>(`/b2b/users/by-customer/${customerId}`);
      setUsers(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (): Promise<void> => {
    if (!email) {
      push({ tone: 'warning', message: t('adminScreens.customerB2bUsers.emailRequired') });
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post<
        B2bUser & { tempPassword?: string; emailSent?: boolean; emailError?: string }
      >(
        '/b2b/users',
        { customerId, email, role, fullName: fullName || undefined },
        { headers: { 'X-Command-Id': cid() } }
      );
      if (data.tempPassword) {
        // Not a toast: a password needs to outlive a glance (see TempPasswordPanel).
        setIssued({
          email: data.email,
          password: data.tempPassword,
          emailSent: data.emailSent,
          emailError: data.emailError,
        });
      }
      push({ tone: 'success', message: t('adminScreens.customerB2bUsers.created') });
      setEmail('');
      setFullName('');
      setRole('B2B_BUYER');
      setShowCreate(false);
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  /** Switch an account off (or back on) without touching what it has already done. */
  const setActive = async (u: B2bUser, active: boolean): Promise<void> => {
    if (!active) {
      const ok = await confirm({
        title: t('adminScreens.customerB2bUsers.deactivateConfirm.title', {
          name: u.full_name || u.email,
        }),
        description: t('adminScreens.customerB2bUsers.deactivateConfirm.description'),
        confirmLabel: t('adminScreens.customerB2bUsers.deactivate'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await apiClient.patch(
        `/b2b/users/${u.id}/active`,
        { active },
        { headers: { 'X-Command-Id': cid() } }
      );
      push({
        tone: 'success',
        message: active
          ? t('adminScreens.customerB2bUsers.reactivated')
          : t('adminScreens.customerB2bUsers.deactivated'),
      });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  /** Only offered for an account that has never been used — the server refuses the rest. */
  const remove = async (u: B2bUser): Promise<void> => {
    const ok = await confirm({
      title: t('adminScreens.customerB2bUsers.deleteConfirm.title', {
        name: u.full_name || u.email,
      }),
      description: t('adminScreens.customerB2bUsers.deleteConfirm.description'),
      confirmLabel: t('adminScreens.customerB2bUsers.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiClient.delete(`/b2b/users/${u.id}`, { headers: { 'X-Command-Id': cid() } });
      push({ tone: 'success', message: t('adminScreens.customerB2bUsers.deleted') });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (u: B2bUser): Promise<void> => {
    const ok = await confirm({
      title: t('adminScreens.customerB2bUsers.resetConfirm.title', {
        name: u.full_name || u.email,
      }),
      description: t('adminScreens.customerB2bUsers.resetConfirm.description'),
      confirmLabel: t('adminScreens.customerB2bUsers.resetPassword'),
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<{
        email: string;
        tempPassword: string;
        emailSent?: boolean;
        emailError?: string;
      }>(`/b2b/users/${u.id}/reset-password`, {}, { headers: { 'X-Command-Id': cid() } });
      setIssued({
        email: data.email,
        password: data.tempPassword,
        emailSent: data.emailSent,
        emailError: data.emailError,
      });
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Hand the owner seat over. A company holds one account owner and must never hold none, so
   * this is the move that lets an owner leave: the incumbent steps down to buyer as this account
   * steps up, in one go.
   */
  const makeOwner = async (u: B2bUser): Promise<void> => {
    const incumbent = users.find((x) => x.role === 'B2B_OWNER' && x.active && x.id !== u.id);
    const ok = await confirm({
      title: t('adminScreens.customerB2bUsers.ownerConfirm.title', {
        name: u.full_name || u.email,
      }),
      description: incumbent
        ? t('adminScreens.customerB2bUsers.ownerConfirm.descriptionIncumbent', {
            name: incumbent.full_name || incumbent.email,
          })
        : t('adminScreens.customerB2bUsers.ownerConfirm.description'),
      confirmLabel: t('adminScreens.customerB2bUsers.ownerConfirm.confirm'),
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiClient.patch(
        `/b2b/users/${u.id}/role`,
        { role: 'B2B_OWNER' },
        { headers: { 'X-Command-Id': cid() } }
      );
      push({ tone: 'success', message: t('adminScreens.customerB2bUsers.ownerChanged') });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  /** "3 orders, 12 audited actions" — why Delete is not on offer for this row. */
  const historyLabel = (u: B2bUser): string =>
    Object.entries(u.history ?? {})
      .map(([label, n]) =>
        HISTORY_KEY[label]
          ? t(`adminScreens.customerB2bUsers.history.${HISTORY_KEY[label]}`, { count: n })
          : `${n} ${label}`
      )
      .join(', ');

  return (
    <div>
      {confirmDialog}
      {issued && (
        <TempPasswordPanel
          email={issued.email}
          password={issued.password}
          emailSent={issued.emailSent}
          emailError={issued.emailError}
          onDismiss={() => setIssued(null)}
        />
      )}
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('adminScreens.customerB2bUsers.intro')}</p>
        {canCreate && (
          <Button size="sm" onClick={() => setShowCreate((v) => !v)}>
            <UserPlus className="h-4 w-4" />
            {t('adminScreens.customerB2bUsers.newUser')}
          </Button>
        )}
      </div>

      {canCreate && showCreate && (
        <div className="mb-4 rounded-lg border bg-card p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="b2b-email">{t('adminScreens.customerB2bUsers.email')}</Label>
              <Input
                id="b2b-email"
                type="email"
                placeholder={t('adminScreens.customerB2bUsers.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="b2b-name">{t('adminScreens.customerB2bUsers.fullName')}</Label>
              <Input
                id="b2b-name"
                placeholder={t('adminScreens.customerB2bUsers.optional')}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="b2b-role">{t('adminScreens.customerB2bUsers.role')}</Label>
              <Select
                id="b2b-role"
                value={role}
                onChange={(e) => setRole(e.target.value as B2bRole)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {t(r.label)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex items-end">
              <Button className="w-full" onClick={create} disabled={busy}>
                <UserPlus className="h-4 w-4" />
                {busy
                  ? t('adminScreens.customerB2bUsers.creating')
                  : t('adminScreens.customerB2bUsers.createUser')}
              </Button>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {t('adminScreens.customerB2bUsers.createHint', {
              blurb: t(ROLE_OPTIONS.find((r) => r.value === role)?.blurb ?? ''),
            })}
          </p>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">
          {t('adminScreens.customerB2bUsers.loading')}
        </p>
      ) : users.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('adminScreens.customerB2bUsers.empty')}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="border-b text-muted-foreground">
            <tr>
              <th className="py-1 text-left">{t('adminScreens.customerB2bUsers.columns.user')}</th>
              <th className="text-left">{t('adminScreens.customerB2bUsers.columns.role')}</th>
              <th className="text-left">{t('adminScreens.customerB2bUsers.columns.status')}</th>
              <th className="text-right">{t('adminScreens.customerB2bUsers.columns.created')}</th>
              {canCreate && (
                <th className="text-right">{t('adminScreens.customerB2bUsers.columns.actions')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t">
                <td className="py-1.5">
                  <div className="font-medium">{u.full_name || u.email}</div>
                  {u.full_name && <div className="text-xs text-muted-foreground">{u.email}</div>}
                </td>
                <td>
                  <Badge variant="secondary">
                    {(() => {
                      const opt = ROLE_OPTIONS.find((r) => r.value === u.role);
                      return opt ? t(opt.label) : u.role;
                    })()}
                  </Badge>
                </td>
                <td>
                  <Badge variant={u.active ? 'success' : 'muted'}>
                    {u.active
                      ? t('adminScreens.customerB2bUsers.active')
                      : t('adminScreens.customerB2bUsers.inactive')}
                  </Badge>
                </td>
                <td className="text-right text-muted-foreground">
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                {canCreate && (
                  <td className="py-1.5 text-right">
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        title={t('adminScreens.customerB2bUsers.resetTitle')}
                        onClick={() => void resetPassword(u)}
                      >
                        <KeyRound className="h-4 w-4" />
                        {t('adminScreens.customerB2bUsers.resetPassword')}
                      </Button>

                      {u.active && u.role !== 'B2B_OWNER' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          title={t('adminScreens.customerB2bUsers.makeOwnerTitle')}
                          onClick={() => void makeOwner(u)}
                        >
                          <ShieldCheck className="h-4 w-4" />
                          {t('adminScreens.customerB2bUsers.makeOwner')}
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy || (u.active && u.lastActiveOwner === true)}
                        title={
                          u.active && u.lastActiveOwner
                            ? t('adminScreens.customerB2bUsers.lastOwnerTitle')
                            : u.active
                              ? t('adminScreens.customerB2bUsers.deactivateTitle')
                              : t('adminScreens.customerB2bUsers.reactivateTitle')
                        }
                        onClick={() => void setActive(u, !u.active)}
                      >
                        <Power className="h-4 w-4" />
                        {u.active
                          ? t('adminScreens.customerB2bUsers.deactivate')
                          : t('adminScreens.customerB2bUsers.reactivate')}
                      </Button>

                      {/* Delete is offered ONLY for an account that has never been used. For any
                          other, the same button would be a 422 the operator has to read: the
                          tooltip says what it did instead. */}
                      {u.deletable === true ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy || (u.active && u.lastActiveOwner === true)}
                          title={t('adminScreens.customerB2bUsers.deleteTitle')}
                          onClick={() => void remove(u)}
                        >
                          <Trash2 className="h-4 w-4" />
                          {t('adminScreens.customerB2bUsers.delete')}
                        </Button>
                      ) : (
                        <span
                          className="self-center px-2 text-xs text-muted-foreground"
                          title={t('adminScreens.customerB2bUsers.inUseTitle', {
                            history: historyLabel(u),
                          })}
                        >
                          {t('adminScreens.customerB2bUsers.inUse')}
                        </span>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
